/**
 * WorkflowNodeGenerator
 *
 * Service for converting AI suggestions into ReactFlow nodes and edges.
 * Handles node creation, positioning, and workflow integration.
 */

import { Node, Edge } from 'reactflow';
import { v4 as uuidv4 } from 'uuid';
import {
  autoLayoutWorkflow,
  createSequentialEdges,
  suggestNodePosition,
} from '@/utils/workflow-auto-layout';
import { SuggestedNode, SuggestedEdge, WorkflowSuggestion, NodeModification } from '@/stores/workflow/workflow-chat-store';
import { workflowFetch } from '@/services/api/workflowApi';

/**
 * Node definition from the node registry
 */
export interface NodeRegistryEntry {
  type: string;
  name: string;
  description?: string;
  category: string;
  icon?: string;
  color?: string;
  inputs?: Array<{ name: string; type: string }>;
  outputs?: Array<{ name: string; type: string }>;
  properties?: Array<{
    name: string;
    type: string;
    label?: string;
    default?: unknown;
    required?: boolean;
  }>;
}

/**
 * Options for generating nodes
 */
export interface GenerateNodesOptions {
  /** Existing nodes in the workflow */
  existingNodes?: Node[];
  /** Existing edges in the workflow */
  existingEdges?: Edge[];
  /** Whether to auto-layout all nodes after adding */
  autoLayout?: boolean;
  /** Node to connect the new nodes to */
  connectToNodeId?: string;
  /** Position hint for placement */
  positionHint?: 'after' | 'before' | 'parallel';
}

/**
 * Result of node generation
 */
export interface GenerateNodesResult {
  /** Generated nodes */
  nodes: Node[];
  /** Generated edges */
  edges: Edge[];
  /** All nodes after merging with existing */
  allNodes: Node[];
  /** All edges after merging with existing */
  allEdges: Edge[];
}

/**
 * Result of applying node modifications
 */
export interface ApplyModificationsResult {
  /** Updated nodes array */
  nodes: Node[];
  /** IDs of nodes that were modified */
  modifiedNodeIds: string[];
  /** Summary of changes made */
  changesSummary: string[];
}

/**
 * Default node colors by category
 */
const CATEGORY_COLORS: Record<string, string> = {
  control: '#6366f1', // Indigo
  variables: '#8b5cf6', // Purple
  timing: '#f59e0b', // Amber
  integrations: '#3b82f6', // Blue
  notifications: '#10b981', // Emerald
  database: '#ef4444', // Red
  analytics: '#06b6d4', // Cyan
  human_workflow: '#f97316', // Orange
  gpio: '#84cc16', // Lime
  sensors: '#14b8a6', // Teal
  communication: '#a855f7', // Fuchsia
  display: '#ec4899', // Pink
  input: '#eab308', // Yellow
  system: '#64748b', // Slate
};

/**
 * Default node dimensions
 */
const NODE_WIDTH = 180;
const NODE_HEIGHT = 60;

/**
 * WorkflowNodeGenerator class
 */
class WorkflowNodeGenerator {
  private nodeRegistry: Map<string, NodeRegistryEntry> = new Map();
  private registryLoaded = false;

  /**
   * Load node registry from backend
   */
  async loadNodeRegistry(): Promise<void> {
    if (this.registryLoaded) return;

    try {
      const response = await workflowFetch('api/nodes');

      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.data)) {
          for (const node of data.data) {
            this.nodeRegistry.set(node.type, node);
          }
          this.registryLoaded = true;
        }
      }
    } catch (error) {
      console.error('Failed to load node registry:', error);
    }
  }

  /**
   * Get node definition from registry
   */
  getNodeDefinition(type: string): NodeRegistryEntry | undefined {
    return this.nodeRegistry.get(type);
  }

  /**
   * Get color for a node type
   */
  getNodeColor(type: string): string {
    const def = this.nodeRegistry.get(type);
    if (def?.color) return def.color;

    const category = def?.category?.toLowerCase() || '';
    return CATEGORY_COLORS[category] || '#6b7280';
  }

  /**
   * Create a ReactFlow node from a suggested node
   */
  createNode(
    suggestion: SuggestedNode,
    position: { x: number; y: number }
  ): Node {
    const id = `node-${uuidv4()}`;
    const def = this.nodeRegistry.get(suggestion.type);

    // Build initial config from defaults and suggestion
    const config: Record<string, unknown> = {};
    if (def?.properties) {
      for (const prop of def.properties) {
        if (prop.default !== undefined) {
          config[prop.name] = prop.default;
        }
      }
    }
    // Override with suggestion config
    if (suggestion.config) {
      Object.assign(config, suggestion.config);
    }

    return {
      id,
      type: 'custom', // ReactFlow node type - always 'custom' for workflow nodes
      position,
      data: {
        label: suggestion.name || def?.name || suggestion.type,
        type: suggestion.type, // Actual node type stored in data
        config,
        parameters: {}, // Initialize empty parameters
        // Store additional metadata
        description: suggestion.description || def?.description,
        category: def?.category,
        icon: def?.icon,
        color: this.getNodeColor(suggestion.type),
      },
    };
  }

  /**
   * Create edges from suggested edges
   * Supports both type-based matching and explicit node IDs
   */
  createEdges(
    suggestedEdges: SuggestedEdge[],
    nodes: Node[],
    newNodeIdMap?: Map<string, string>
  ): Edge[] {
    const edges: Edge[] = [];

    // Build a map of node types to node IDs
    const typeToNodeIds = new Map<string, string[]>();
    for (const node of nodes) {
      const type = node.data?.type || node.type;
      if (!typeToNodeIds.has(type)) {
        typeToNodeIds.set(type, []);
      }
      const nodeIds = typeToNodeIds.get(type);
      if (nodeIds) {
        nodeIds.push(node.id);
      }
    }

    // Resolve __NEW_NODE__ or __NEW_NODE_N__ references to actual node IDs
    const resolveNewNodeRef = (ref: string): string | undefined => {
      if (!ref.startsWith('__NEW_NODE')) return undefined;
      // Plain __NEW_NODE__ — backward compat, resolves to first new node
      if (ref === '__NEW_NODE__') return newNodeIdMap?.values().next().value;
      // Indexed __NEW_NODE_0__, __NEW_NODE_1__, etc.
      const match = ref.match(/^__NEW_NODE_(\d+)__$/);
      if (match) return newNodeIdMap?.get(match[1]);
      return undefined;
    };

    for (const suggestedEdge of suggestedEdges) {
      let sourceId: string | undefined;
      let targetId: string | undefined;

      // Use explicit node IDs if provided
      if (suggestedEdge.sourceNodeId) {
        const resolved = resolveNewNodeRef(suggestedEdge.sourceNodeId);
        if (resolved) {
          sourceId = resolved;
        } else {
          sourceId = suggestedEdge.sourceNodeId;
        }
      } else {
        // Fall back to type-based matching
        const sourceIds = typeToNodeIds.get(suggestedEdge.sourceType) || [];
        sourceId = sourceIds[sourceIds.length - 1]; // Use last match
      }

      if (suggestedEdge.targetNodeId) {
        const resolved = resolveNewNodeRef(suggestedEdge.targetNodeId);
        if (resolved) {
          targetId = resolved;
        } else {
          targetId = suggestedEdge.targetNodeId;
        }
      } else {
        // Fall back to type-based matching
        const targetIds = typeToNodeIds.get(suggestedEdge.targetType) || [];
        targetId = targetIds[0]; // Use first match
      }

      // Create edge if both source and target are found
      if (sourceId && targetId) {
        edges.push({
          id: `edge-${sourceId}-${targetId}-${uuidv4().slice(0, 8)}`,
          source: sourceId,
          target: targetId,
          sourceHandle: suggestedEdge.sourceHandle,
          targetHandle: suggestedEdge.targetHandle,
          type: 'smoothstep',
        });
      }
    }

    return edges;
  }

  /**
   * Generate ReactFlow nodes and edges from a workflow suggestion
   */
  generateFromSuggestion(
    suggestion: WorkflowSuggestion,
    options: GenerateNodesOptions = {}
  ): GenerateNodesResult {
    const {
      existingNodes = [],
      existingEdges = [],
      autoLayout = true,
      connectToNodeId,
      positionHint = 'after',
    } = options;

    // Check if we have insertion hints from the suggestion
    const firstNode = suggestion.nodes[0];
    const insertAfterNodeId = firstNode?.insertAfterNodeId;
    const insertBeforeNodeId = firstNode?.insertBeforeNodeId;

    // Determine the reference node for positioning
    let referenceNodeId = connectToNodeId;
    if (insertAfterNodeId) {
      referenceNodeId = insertAfterNodeId;
    } else if (insertBeforeNodeId) {
      // For "before" insertion, find the node that connects TO the target
      const incomingEdge = existingEdges.find(e => e.target === insertBeforeNodeId);
      referenceNodeId = incomingEdge?.source;
    }

    // Generate nodes
    const newNodes: Node[] = [];
    const newNodeIdMap = new Map<string, string>(); // Map suggestion index to actual node ID

    let lastPosition = suggestNodePosition(
      existingNodes,
      existingEdges,
      referenceNodeId,
      insertBeforeNodeId ? 'before' : positionHint
    );

    for (let i = 0; i < suggestion.nodes.length; i++) {
      const suggestedNode = suggestion.nodes[i];
      const node = this.createNode(suggestedNode, lastPosition);
      newNodes.push(node);
      newNodeIdMap.set(`${i}`, node.id);

      // Update position for next node
      lastPosition = {
        x: lastPosition.x,
        y: lastPosition.y + NODE_HEIGHT + 80,
      };
    }

    // Start with existing edges, then remove any that should be removed
    let filteredExistingEdges = [...existingEdges];
    if (suggestion.edgesToRemove && suggestion.edgesToRemove.length > 0) {
      const edgesToRemoveSet = new Set(suggestion.edgesToRemove);
      filteredExistingEdges = existingEdges.filter(e => !edgesToRemoveSet.has(e.id));
    }

    // Generate edges
    let newEdges: Edge[];
    if (suggestion.edges && suggestion.edges.length > 0) {
      // Use suggested edges with node ID mapping
      // All nodes (existing + new) for edge creation
      const allNodesForEdges = [...existingNodes, ...newNodes];
      newEdges = this.createEdges(suggestion.edges, allNodesForEdges, newNodeIdMap);
    } else {
      // Create sequential edges for new nodes
      newEdges = createSequentialEdges(newNodes);

      // Connect to existing workflow if specified and no explicit edges
      if (referenceNodeId && newNodes.length > 0) {
        // Check if there's an existing outgoing edge from the reference node
        const existingOutgoingEdge = existingEdges.find(e => e.source === referenceNodeId);

        if (existingOutgoingEdge && insertAfterNodeId) {
          // Insert between: connect reference -> new node -> original target
          // The original edge should already be in edgesToRemove
          const lastNewNode = newNodes[newNodes.length - 1];

          // Edge from new node to original target
          newEdges.push({
            id: `edge-${lastNewNode.id}-${existingOutgoingEdge.target}`,
            source: lastNewNode.id,
            target: existingOutgoingEdge.target,
            sourceHandle: 'source-right',
            targetHandle: existingOutgoingEdge.targetHandle || 'target-left',
            type: 'smoothstep',
          });
        }

        // Edge from reference to first new node
        const connectionEdge: Edge = {
          id: `edge-${referenceNodeId}-${newNodes[0].id}`,
          source: referenceNodeId,
          target: newNodes[0].id,
          sourceHandle: 'source-right',
          targetHandle: 'target-left',
          type: 'smoothstep',
        };
        newEdges.unshift(connectionEdge);
      }
    }

    // Merge with filtered existing edges
    let allNodes = [...existingNodes, ...newNodes];
    let allEdges = [...filteredExistingEdges, ...newEdges];

    // Apply auto-layout if requested
    if (autoLayout && allNodes.length > 0) {
      allNodes = autoLayoutWorkflow(allNodes, allEdges, {
        direction: 'TB',
        nodeSpacingX: 200,
        nodeSpacingY: 100,
      });
    }

    return {
      nodes: newNodes,
      edges: newEdges,
      allNodes,
      allEdges,
    };
  }

  /**
   * Apply modifications to existing nodes (rename, update config)
   */
  applyNodeModifications(
    existingNodes: Node[],
    modifications: NodeModification[]
  ): ApplyModificationsResult {
    const modifiedNodeIds: string[] = [];
    const changesSummary: string[] = [];

    // Create a new array to avoid mutating the original
    const updatedNodes = existingNodes.map(node => {
      // Find if this node has a modification
      const modification = modifications.find(m => m.nodeId === node.id);

      if (!modification) {
        return node; // No changes to this node
      }

      // Track that we're modifying this node
      modifiedNodeIds.push(node.id);
      changesSummary.push(modification.changeDescription);

      // Clone the node to avoid mutation
      const updatedNode = {
        ...node,
        data: { ...node.data },
      };

      // Apply label change
      if (modification.newLabel) {
        updatedNode.data.label = modification.newLabel;
      }

      // Apply config updates (merge with existing config)
      if (modification.configUpdates) {
        updatedNode.data.config = {
          ...(updatedNode.data.config || {}),
          ...modification.configUpdates,
        };
      }

      return updatedNode;
    });

    return {
      nodes: updatedNodes,
      modifiedNodeIds,
      changesSummary,
    };
  }

  /**
   * Generate a simple node by type
   */
  generateNode(
    type: string,
    options: {
      name?: string;
      config?: Record<string, unknown>;
      position?: { x: number; y: number };
    } = {}
  ): Node {
    const def = this.nodeRegistry.get(type);
    const position = options.position || { x: 100, y: 100 };

    return this.createNode(
      {
        type,
        name: options.name || def?.name || type,
        config: options.config,
      },
      position
    );
  }

  /**
   * Generate start and end nodes for a new workflow
   */
  generateWorkflowSkeleton(): { nodes: Node[]; edges: Edge[] } {
    const startNode = this.createNode(
      { type: 'start', name: 'Start' },
      { x: 250, y: 50 }
    );

    const endNode = this.createNode(
      { type: 'end', name: 'End' },
      { x: 250, y: 250 }
    );

    const edge: Edge = {
      id: `edge-${startNode.id}-${endNode.id}`,
      source: startNode.id,
      target: endNode.id,
      type: 'smoothstep',
    };

    return {
      nodes: [startNode, endNode],
      edges: [edge],
    };
  }

  /**
   * Find the best position to insert a node in an existing workflow
   */
  findInsertPosition(
    existingNodes: Node[],
    existingEdges: Edge[],
    afterNodeId?: string
  ): { x: number; y: number } {
    return suggestNodePosition(
      existingNodes,
      existingEdges,
      afterNodeId,
      'after'
    );
  }

  /**
   * Validate that a node type exists in the registry
   */
  isValidNodeType(type: string): boolean {
    return this.nodeRegistry.has(type);
  }

  /**
   * Get all available node types
   */
  getAvailableNodeTypes(): string[] {
    return Array.from(this.nodeRegistry.keys());
  }

  /**
   * Get nodes by category
   */
  getNodesByCategory(): Map<string, NodeRegistryEntry[]> {
    const categories = new Map<string, NodeRegistryEntry[]>();

    for (const [_, node] of this.nodeRegistry) {
      const category = node.category || 'other';
      if (!categories.has(category)) {
        categories.set(category, []);
      }
      const nodes = categories.get(category);
      if (nodes) {
        nodes.push(node);
      }
    }

    return categories;
  }
}

// Export singleton instance
export const workflowNodeGenerator = new WorkflowNodeGenerator();

// Initialize on import (lazy load)
workflowNodeGenerator.loadNodeRegistry().catch(console.error);

export default workflowNodeGenerator;
